import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Dumbbell, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Subscriptions = () => {
  const { isAdmin } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSub, setEditingSub] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    classes: '',
    duration_days: '30',
    price: ''
  });

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/subscriptions`);
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Error al cargar suscripciones');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        classes: parseInt(formData.classes),
        duration_days: parseInt(formData.duration_days),
        price: parseFloat(formData.price)
      };

      if (editingSub) {
        await axios.put(`${API}/subscriptions/${editingSub.id}`, data);
        toast.success('Suscripción actualizada');
      } else {
        await axios.post(`${API}/subscriptions`, data);
        toast.success('Suscripción creada');
      }
      
      setShowModal(false);
      resetForm();
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar suscripción');
    }
  };

  const handleEdit = (sub) => {
    setEditingSub(sub);
    setFormData({
      name: sub.name,
      classes: sub.classes.toString(),
      duration_days: sub.duration_days.toString(),
      price: sub.price.toString()
    });
    setShowModal(true);
  };

  const handleDelete = async (subId) => {
    if (!window.confirm('¿Eliminar esta suscripción?')) return;
    
    try {
      await axios.delete(`${API}/subscriptions/${subId}`);
      toast.success('Suscripción eliminada');
      fetchSubscriptions();
    } catch (error) {
      toast.error('Error al eliminar suscripción');
    }
  };

  const resetForm = () => {
    setEditingSub(null);
    setFormData({
      name: '',
      classes: '',
      duration_days: '30',
      price: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  return (
    <div data-testid="subscriptions-page" className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Suscripciones</h1>
            <p className="text-zinc-500 text-sm">{subscriptions.length} paquetes</p>
          </div>
          <Button
            onClick={fetchSubscriptions}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
        {isAdmin() && (
          <Button
            data-testid="add-subscription-btn"
            onClick={() => { resetForm(); setShowModal(true); }}
            className="bg-[#FF3B30] hover:bg-[#D32F2F] text-white font-bold uppercase tracking-wider rounded-sm primary-glow text-sm"
          >
            <Plus size={16} className="mr-2" />
            Nueva
          </Button>
        )}
      </div>

      {/* Subscriptions Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
        {subscriptions.map((sub) => (
          <Card
            key={sub.id}
            data-testid={`subscription-${sub.id}`}
            className="bg-[#0A0A0A] border-zinc-800 hover:border-[#FF3B30]/30 transition-colors"
          >
            <CardContent className="p-4 sm:p-6 text-center">
              <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-[#FF3B30]/10 rounded-sm flex items-center justify-center mb-3 sm:mb-4">
                <Dumbbell className="text-[#FF3B30]" size={24} />
              </div>
              <h3 className="text-sm sm:text-lg font-bold text-white mb-2 truncate">{sub.name}</h3>
              <div className="space-y-1 mb-3 sm:mb-4">
                <p className="text-2xl sm:text-3xl font-mono font-black text-[#FF3B30]">
                  {sub.classes}
                </p>
                <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider">clases</p>
              </div>
              <p className="text-xs text-zinc-400 mb-3 sm:mb-4">
                {sub.duration_days} días
              </p>
              <p className="text-lg sm:text-2xl font-mono font-bold text-white">
                {formatCurrency(sub.price)}
              </p>
              
              {isAdmin() && (
                <div className="flex gap-2 mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-zinc-800">
                  <Button
                    data-testid={`edit-sub-${sub.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(sub)}
                    className="flex-1 border-zinc-700 hover:bg-zinc-800 text-xs h-8"
                  >
                    <Pencil size={12} className="mr-1" />
                    Editar
                  </Button>
                  <Button
                    data-testid={`delete-sub-${sub.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(sub.id)}
                    className="border-red-900 text-red-500 hover:bg-red-500/10 h-8 px-2"
                  >
                    <Trash2 size={12} />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {subscriptions.length === 0 && (
        <div className="text-center py-12">
          <Dumbbell size={48} className="mx-auto text-zinc-700 mb-4" />
          <p className="text-zinc-500">No hay suscripciones</p>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0A0A0A] border-zinc-800 max-w-md mx-4">
          <DialogHeader>
            <DialogTitle className="text-white">
              {editingSub ? 'Editar Suscripción' : 'Nueva Suscripción'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-400">Nombre</Label>
              <Input
                data-testid="sub-name-input"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Ej: Paquete Básico"
                required
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Clases</Label>
                <Input
                  data-testid="sub-classes-input"
                  type="number"
                  value={formData.classes}
                  onChange={(e) => setFormData({...formData, classes: e.target.value})}
                  placeholder="4, 8, 12..."
                  required
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Días</Label>
                <Input
                  data-testid="sub-duration-input"
                  type="number"
                  value={formData.duration_days}
                  onChange={(e) => setFormData({...formData, duration_days: e.target.value})}
                  required
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400">Precio</Label>
              <Input
                data-testid="sub-price-input"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                required
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowModal(false)}
                className="border-zinc-700 w-full sm:w-auto"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                data-testid="save-sub-btn"
                className="bg-[#FF3B30] hover:bg-[#D32F2F] w-full sm:w-auto"
              >
                {editingSub ? 'Guardar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subscriptions;
